import { ComponentProps } from "react";
import { GA_MOBILE_REF } from "@pages/Home/Overview/utils/constants";
import Card from "@particles/card/Card";
import CardGroupedListItem from "@particles/card/components/CardGroupedList/CardGroupedListItem";
import { getMetaDetails } from "@particles/card/utils/helpers";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import { getUrlQuery, isArticleUpdated } from "@util/helpers";

/**
 * Returns the card props for the Home top stories component.
 * @param articles - The articles to be displayed in the component.
 * @returns {ComponentProps<typeof Card>[]} - The card props for the Home top stories component.
 */
export const getTopStoriesProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof Card>[] => {
  const cards = articles
    .slice(0, 9)
    .map(
      (
        {
          id,
          title,
          urlPath,
          media,
          kicker,
          displaySetting,
          paywall,
          sections,
          updated,
          edited,
        },
        index
      ) => {
        const mainSection = sections?.[0];
        const sectionUniqueName = mainSection?.uniqueName;
        const _meta = getMetaDetails(mainSection);

        const _edited = isArticleUpdated(updated, edited) ? edited : undefined;
        const _media = [0, 5, 6].includes(index) ? media[0] : undefined;

        const imgSrc = _media?.landscape?.url;
        const imgWidth = _media?.landscape?.width;
        const imgHeight = _media?.landscape?.height;

        const defaultImage =
          sectionUniqueName && [0, 5, 6].includes(index)
            ? { directoryName: sectionUniqueName }
            : undefined;

        const props: ComponentProps<typeof Card> = {
          id,
          title,
          link: urlPath,
          kicker: kicker?.fields?.[0]?.value,
          displayPaywall: paywall?.contentAccess === "1",
          hasUpdatedTimestampDisplay: displaySetting?.hasUpdatedTimeDisplayed,
          meta: _meta,
          editedTime: _edited,
          thumbnail:
            imgSrc && imgWidth && imgHeight
              ? {
                  src: imgSrc,
                  alt: _media?.alt || _media.caption || "",
                  width: imgWidth,
                  height: imgHeight,
                }
              : undefined,
          defaultImage: defaultImage,
          isH1: index === 0,
          displayPlaceholderWhenImageLoading: index === 0,
        };

        return props;
      }
    );

  return JSON.parse(JSON.stringify(cards));
};

/**
 * Returns the card props for the group top stories component.
 * @param articles - The articles to be displayed in the component.
 * @returns {ComponentProps<typeof CardGroupedListItem>[]} - The card props for the Home group top stories component.
 */
export const getGroupedTopStoriesProps = (
  articles: SanitizedFeedSectionArticle[]
): ComponentProps<typeof CardGroupedListItem>[] => {
  const cards = articles.slice(0, 2).map(({ id, title, urlPath }, index) => {
    const ref = `home-grouped-story-${index + 2}`;
    const linkParams = new URLSearchParams({ ref });
    const props: ComponentProps<typeof Card> = {
      id,
      title,
      link: urlPath + getUrlQuery(urlPath, linkParams),
    };

    return props;
  });

  return JSON.parse(JSON.stringify(cards));
};

/**
 * Gets the top stories link with reference parameter for tracking in URL.
 * @param link - The original link of the article.
 * @param index - The index of the article in the top stories list.
 * @param isScreenLG - Boolean indicating if the screen size is large.
 * @returns The modified link with reference parameter if applicable.
 */
export const getTopStoriesLinkWithRef = (
  link: string,
  index: number,
  isScreenLG: boolean
) => {
  if (index === 0) {
    const ref = new URLSearchParams({ ref: "home-grouped-story-1" });
    return link + getUrlQuery(link, ref);
  }

  const defaultRef = `home-top-stories-${index}`;
  const mobileRef = GA_MOBILE_REF[index] ? GA_MOBILE_REF[index] : defaultRef;
  const ref = isScreenLG ? defaultRef : mobileRef;

  const linkParams = new URLSearchParams({ ref });
  return link + getUrlQuery(link, linkParams);
};
